import { createContext, useContext, useState, useEffect, useCallback } from "react";

// Usuarios se cargan desde archivo de configuración o defaults seguros
// En producción, las credenciales se inyectan vía build-time env vars
const INITIAL_USERS = JSON.parse(
  process.env.REACT_APP_USERS || '[{"username":"demo","password":"demo123","full_name":"Demo","role":"viewer"}]'
);

const STORAGE_KEY_SESSION = "uc_auth_session";
const STORAGE_KEY_USERS = "uc_auth_users";
const USERS_VERSION = 5; // Increment this to force localStorage refresh

function loadUsers() {
  try {
    const storedVersion = parseInt(localStorage.getItem("uc_auth_users_version") || "0", 10);
    if (storedVersion >= USERS_VERSION) {
      const stored = localStorage.getItem(STORAGE_KEY_USERS);
      if (stored) return JSON.parse(stored);
    }
  } catch {
    /* corrupted — fall through */
  }
  // Force refresh: store new users and update version
  localStorage.setItem(STORAGE_KEY_USERS, JSON.stringify(INITIAL_USERS));
  localStorage.setItem("uc_auth_users_version", String(USERS_VERSION));
  return INITIAL_USERS;
}

function persistUsers(users) {
  localStorage.setItem(STORAGE_KEY_USERS, JSON.stringify(users));
}

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [users, setUsers] = useState(loadUsers);
  const [isLoading, setIsLoading] = useState(true);

  /* Restore session from localStorage on mount */
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY_SESSION);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed?.username) setUser(parsed);
      }
    } catch {
      localStorage.removeItem(STORAGE_KEY_SESSION);
    }
    setIsLoading(false);
  }, []);

  const login = useCallback(
    (username, password) => {
      const match = users.find(
        (u) => u.username === username && u.password === password,
      );
      if (!match) return false;

      const session = {
        username: match.username,
        full_name: match.full_name,
        role: match.role,
      };
      setUser(session);
      localStorage.setItem(STORAGE_KEY_SESSION, JSON.stringify(session));
      return true;
    },
    [users],
  );

  const logout = useCallback(() => {
    setUser(null);
    localStorage.removeItem(STORAGE_KEY_SESSION);
  }, []);

  const addUser = useCallback((newUser) => {
    setUsers((prev) => {
      const exists = prev.some((u) => u.username === newUser.username);
      if (exists) return prev;
      const updated = [...prev, newUser];
      persistUsers(updated);
      return updated;
    });
  }, []);

  const getUsers = useCallback(() => users, [users]);

  const isAuthenticated = !!user;

  return (
    <AuthContext.Provider
      value={{ user, isAuthenticated, isLoading, login, logout, addUser, getUsers }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
